// Admin subscription payments — log + refund action.
//
// Each row is one Stripe charge. Refunding fires a Stripe refund on
// the server (POST /admin/subscription-payments/:id/refund). Stripe
// then fires `charge.refunded` and the existing webhook handler
// reverses the earnings via process_subscription_refund. The earnings
// reversal is the single source of truth, so this UI just kicks off
// the Stripe refund and trusts the webhook.
import { useCallback, useEffect, useState } from "react";
import { Button } from "../../components/ui/Button";
import { Card, CardSubtle, CardTitle } from "../../components/ui/Card";
import { Input, Label, Select, Textarea } from "../../components/ui/Input";
import { Modal } from "../../components/ui/Modal";
import { Spinner } from "../../components/ui/Spinner";
import { api } from "../../lib/api";

interface PaymentRow {
  id: string;
  subscription_id: string;
  stripe_payment_intent_id: string;
  amount_charged_usd: number;
  amount_charged_original: number;
  currency_original: string;
  stripe_fee_estimated_usd: number;
  stripe_fee_actual_usd: number | null;
  net_revenue_usd: number;
  owner_share_usd: number;
  moderator_pool_usd: number;
  status: string;
  charged_at: string;
  refunded_at: string | null;
  refund_amount_usd: number | null;
  subscriptions: {
    id: string;
    subscriber_id: string;
    community_id: string;
    communities: { slug: string; name: string } | null;
    profiles: { username: string } | null;
  } | null;
}

interface Stats {
  owner_share_total_usd: number;
  moderator_pool_total_usd: number;
}

interface ListResponse {
  payments: PaymentRow[];
  total: number;
  stats: Stats;
}

const STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "succeeded", label: "Succeeded" },
  { value: "failed", label: "Failed" },
  { value: "refunded", label: "Refunded" },
  { value: "partially_refunded", label: "Partially refunded" },
];

function formatUsd(n: number | null): string {
  if (n == null) return "—";
  return `$${Number(n).toFixed(2)}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function statusColor(s: string): string {
  switch (s) {
    case "succeeded":
      return "var(--upvote, #22C55E)";
    case "failed":
      return "var(--danger, #ef4444)";
    case "refunded":
    case "partially_refunded":
      return "var(--text-secondary)";
    default:
      return "var(--text-primary)";
  }
}

export default function AdminSubscriptionPaymentsPage() {
  const [data, setData] = useState<ListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const [refundTarget, setRefundTarget] = useState<PaymentRow | null>(null);
  const [refundReason, setRefundReason] = useState("");
  const [refundSubmitting, setRefundSubmitting] = useState(false);
  const [refundError, setRefundError] = useState<string | null>(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      params.set("limit", "100");
      const res = await api<ListResponse>(
        `/admin/subscription-payments?${params.toString()}`,
      );
      setData(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, from, to]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  async function submitRefund() {
    if (!refundTarget) return;
    setRefundSubmitting(true);
    setRefundError(null);
    try {
      await api(`/admin/subscription-payments/${refundTarget.id}/refund`, {
        method: "POST",
        body: { reason: refundReason.trim() },
      });
      setRefundTarget(null);
      setRefundReason("");
      await fetchList();
    } catch (err) {
      setRefundError(err instanceof Error ? err.message : "Refund failed");
    } finally {
      setRefundSubmitting(false);
    }
  }

  const rows = data?.payments ?? [];

  return (
    <div>
      <div style={styles.header}>
        <h1 style={styles.h1}>Subscription payments</h1>
        <CardSubtle>
          Every subscription charge. Refunds fire Stripe refunds; the
          earnings reversal happens automatically via the charge.refunded
          webhook.
        </CardSubtle>
      </div>

      <div style={styles.statsRow}>
        <Card style={{ flex: 1 }}>
          <CardSubtle>Owner share (filtered)</CardSubtle>
          <div style={styles.statValue}>
            {formatUsd(data?.stats.owner_share_total_usd ?? 0)}
          </div>
        </Card>
        <Card style={{ flex: 1 }}>
          <CardSubtle>Moderator pool (filtered)</CardSubtle>
          <div style={styles.statValue}>
            {formatUsd(data?.stats.moderator_pool_total_usd ?? 0)}
          </div>
        </Card>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <div style={styles.filterRow}>
          <div style={{ flex: 1 }}>
            <Label>Status</Label>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </div>
          <div style={{ flex: 1 }}>
            <Label>From</Label>
            <Input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div style={{ flex: 1 }}>
            <Label>To</Label>
            <Input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
        </div>
      </Card>

      {loading ? (
        <Card>
          <div style={styles.loadingWrap}>
            <Spinner />
          </div>
        </Card>
      ) : error ? (
        <Card>
          <div style={styles.errorBar}>{error}</div>
        </Card>
      ) : rows.length === 0 ? (
        <Card>
          <CardTitle>No payments</CardTitle>
          <CardSubtle>Nothing matches the current filters.</CardSubtle>
        </Card>
      ) : (
        <Card padding={0}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.tableHead}>
                <Th>Subscriber</Th>
                <Th>Community</Th>
                <Th>Gross</Th>
                <Th>Fee est / actual</Th>
                <Th>Owner</Th>
                <Th>Mod pool</Th>
                <Th>Status</Th>
                <Th>Charged</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const sub = row.subscriptions;
                return (
                  <tr key={row.id} style={styles.tableRow}>
                    <Td>@{sub?.profiles?.username ?? "?"}</Td>
                    <Td>r/{sub?.communities?.slug ?? "?"}</Td>
                    <Td>
                      <strong>{formatUsd(row.amount_charged_usd)}</strong>
                      {row.currency_original !== "USD" && (
                        <CardSubtle style={{ fontSize: 12 }}>
                          {row.amount_charged_original} {row.currency_original}
                        </CardSubtle>
                      )}
                    </Td>
                    <Td>
                      {formatUsd(row.stripe_fee_estimated_usd)} /{" "}
                      {formatUsd(row.stripe_fee_actual_usd)}
                    </Td>
                    <Td>{formatUsd(row.owner_share_usd)}</Td>
                    <Td>{formatUsd(row.moderator_pool_usd)}</Td>
                    <Td>
                      <span style={{ color: statusColor(row.status) }}>
                        {row.status}
                      </span>
                      {row.refund_amount_usd != null && (
                        <CardSubtle style={{ fontSize: 12 }}>
                          refunded {formatUsd(row.refund_amount_usd)}
                        </CardSubtle>
                      )}
                    </Td>
                    <Td>
                      <CardSubtle style={{ fontSize: 12 }}>
                        {formatDate(row.charged_at)}
                      </CardSubtle>
                    </Td>
                    <Td>
                      {row.status === "succeeded" && (
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => setRefundTarget(row)}
                        >
                          Refund
                        </Button>
                      )}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      <Modal
        open={refundTarget !== null}
        onClose={() => !refundSubmitting && setRefundTarget(null)}
        title={
          refundTarget
            ? `Refund ${formatUsd(refundTarget.amount_charged_usd)} payment from @${refundTarget.subscriptions?.profiles?.username ?? "?"}`
            : ""
        }
      >
        <CardSubtle style={{ marginBottom: 12 }}>
          Fires a Stripe refund. The charge.refunded webhook reverses the
          owner and moderator earnings tied to this payment and debits
          their wallets. Cannot be undone.
        </CardSubtle>
        <Label>Reason</Label>
        <Textarea
          value={refundReason}
          onChange={(e) => setRefundReason(e.target.value)}
          rows={3}
          placeholder="Briefly explain why this payment is being refunded"
        />
        {refundError && (
          <div style={{ ...styles.errorBar, marginTop: 12 }}>{refundError}</div>
        )}
        <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
          <Button
            variant="secondary"
            onClick={() => setRefundTarget(null)}
            disabled={refundSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={submitRefund}
            disabled={refundReason.trim().length < 3 || refundSubmitting}
          >
            {refundSubmitting ? "Refunding…" : "Refund payment"}
          </Button>
        </div>
      </Modal>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th style={styles.th}>{children}</th>;
}
function Td({ children }: { children: React.ReactNode }) {
  return <td style={styles.td}>{children}</td>;
}

const styles: Record<string, React.CSSProperties> = {
  header: { marginBottom: 16 },
  h1: { margin: 0, fontSize: 24, fontWeight: 700, color: "var(--text-primary)" },
  statsRow: { display: "flex", gap: 12, marginBottom: 16 },
  statValue: {
    fontSize: 28,
    fontWeight: 700,
    color: "var(--text-primary)",
    margin: "4px 0",
  },
  filterRow: { display: "flex", gap: 12 },
  loadingWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "48px 0",
  },
  errorBar: {
    background: "rgba(239,68,68,0.1)",
    color: "var(--danger, #ef4444)",
    padding: 12,
    borderRadius: "var(--radius-sm)",
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 14 },
  tableHead: { background: "rgba(255,255,255,0.02)", textAlign: "left" },
  tableRow: { borderTop: "1px solid var(--border)" },
  th: {
    padding: "10px 12px",
    fontSize: 12,
    fontWeight: 600,
    color: "var(--text-secondary)",
    textTransform: "uppercase",
    letterSpacing: 0.3,
  },
  td: { padding: 12, verticalAlign: "top", color: "var(--text-primary)" },
};
